import React, { useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { useDispatch } from 'react-redux'
import { Button, Card, CardContent, Grid2, Typography } from '@mui/material'
import UserAction from '../user/actions/UserAction'
import { snakeCase } from 'lodash'
import ContactAction from 'referencial/components/contact/actions/ContactAction'
import { CardTitle } from 'components/card/NewCard'
import { OBSERVATORY, WIDGET } from './ObservatoryConstants'
import StepperDialog from 'components/modal/StepperDialog'
import ToastrAction from 'toastr/actions/ToastrAction'
import moment from 'moment'
import { DEFAULT_AQUASYS_EMAIL } from 'components/sieau/SieauConstants'
import DefinitionPanel from './DefinitionPanel'

const MAX_LOGIN_LENGTH = 50
const getLimitedLoginLength = (conventionalName = '') => MAX_LOGIN_LENGTH - conventionalName.length - 1
const getLimitedLogin = (login, conventionalName) => login.slice(0, getLimitedLoginLength(conventionalName))

const CREATE_MODULE_STEP = 0
const DEFINED_MODULE_STEP = 1

const CreateModuleDialog = ({
    open = false,
    onClose = () => {},
}) => {
    const [module, setModule] = useState({})

    const dispatch = useDispatch()

    const handleClose = () => {
        onClose()
        dispatch(ToastrAction.success(i18n.moduleSuccessfullySaved))
        dispatch(UserAction.fetchApplicativeUsers())
        dispatch(ContactAction.fetchContacts(true))
        dispatch(UserAction.getUsersModulesAccess())
    }

    const onValidate = () => {
        dispatch(UserAction.addUser({
            login: `${module.conventionalName}_${snakeCase(module.name)}`,
            email: DEFAULT_AQUASYS_EMAIL,
            name: module.name,
            isApplicative: true,
        }, true, handleClose))
    }

    return (
        <StepperDialog
            open={open}
            closeDialog={onClose}
            maxWidth='lg'
            sx={{ minHeight: '65vh', maxHeight: '65vh' }}
            contentStyle={{ flex: '1 1 0' }}
            title={i18n.creatingModule}
            steps={[{
                label: i18n.creatingModule,
                constant: CREATE_MODULE_STEP,
                nextAvailable: !!module.name,
            }, {
                label: i18n.definition,
                constant: DEFINED_MODULE_STEP,
            }]}

            renderSaveButton={step => (step !== CREATE_MODULE_STEP) && (
                <Button
                    variant='contained'
                    onClick={onValidate}
                >
                    {i18n.validate}
                </Button>
            )}
        >
            {step => (
                <Grid2 container sx={{ height: '100%' }} alignItems='center' columnSpacing={0.5}>
                    <Grid2 container size='grow' sx={{ height: '100%', maxHeight: '100%', overflowY: 'auto', paddingRight: 1 }} rowSpacing={1.5}>
                        {(step === CREATE_MODULE_STEP) && (
                            <Grid2 container size={12} direction='column' rowSpacing={1}>
                                <Typography variant='sectionTitle'>{i18n.adminModuleChoiceSubtitle}</Typography>
                                <Card variant='newCard'>
                                    <CardContent>
                                        <Grid2 container spacing={1.5}>
                                            <Grid2 size={6}>
                                                <Card
                                                    variant='newCard'
                                                    clickable
                                                    onClick={() => setModule(prev => ({
                                                        ...prev,
                                                        ...OBSERVATORY,
                                                        module: OBSERVATORY.name,
                                                        name: getLimitedLogin(`${OBSERVATORY.name} ${moment().valueOf()}`, module.conventionalName),
                                                    }))}
                                                    selected={module.module === OBSERVATORY.name}
                                                >
                                                    <CardTitle title={<Typography sx={{ fontSize: '1.25rem', fontWeight: '600' }}>{OBSERVATORY.name}</Typography>} />
                                                    <CardContent>
                                                        {i18n.observatoryDescription.split('\n').map((od, i) => (
                                                            <Typography key={i} color='black80'>{od}</Typography>
                                                        ))}
                                                    </CardContent>
                                                </Card>
                                            </Grid2>
                                            <Grid2 size={6}>
                                                <Card
                                                    variant='newCard'
                                                    clickable
                                                    onClick={() => setModule(prev => ({
                                                        ...prev,
                                                        ...WIDGET,
                                                        module: WIDGET.name,
                                                        name: getLimitedLogin(`${WIDGET.name} ${moment().valueOf()}`, module.conventionalName),
                                                    }))}
                                                    selected={module.module === WIDGET.name}
                                                >
                                                    <CardTitle title={<Typography sx={{ fontSize: '1.25rem', fontWeight: '600' }}>{WIDGET.name}</Typography>} />
                                                    <CardContent>
                                                        <Typography color='black80'>{i18n.widgetDescription}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid2>
                                        </Grid2>
                                    </CardContent>
                                </Card>
                            </Grid2>
                        )}
                        {(step === DEFINED_MODULE_STEP) && (
                            <DefinitionPanel
                                module={module}
                                setModule={setModule}
                            />
                        )}
                    </Grid2>
                </Grid2>
            )}
        </StepperDialog>
    )
}

CreateModuleDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default CreateModuleDialog